import React, { useReducer, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import i18n from 'i18next';

// --- Views & Feature Gates ---
import { Layout } from './components/Layout';
import { Dashboard } from './views/Dashboard';
import { Tasks } from './views/Tasks';
import { Inventory } from './views/Groceries';
import { Maintenance } from './views/Maintenance';
import { Calendar } from './views/Calendar';
import { HouseholdSettings } from './views/HouseholdSettings';
import { UserSettings } from './views/UserSettings';
import { Login } from './views/Login';
import { LandingPage } from './views/LandingPage';
import { PricingPage } from './views/PricingPage';
import { DisclaimerPage, CURRENT_DISCLAIMER_VERSION } from './views/DisclaimerPage';
import { OnboardingLegal, LEGAL_VERSION } from './views/OnboardingLegal';
import { BugReport } from './views/BugReport'; 
import { Changelog } from './views/Changelog';
import PrivacyPolicy from './views/PrivacyPolicy';
import TermsOfService from './views/TermsOfService';

// --- Components & Utilities ---
import { CookieConsent } from './components/CookieConsent';
import { SearchPortal } from './features/search-engine/components/SearchPortal'; 

// --- State & Context ---
import { Role, UserProfile, KanbanTask, AppState, CalendarEvent, Household } from './types';
import { appReducer, initialState } from './reducers/AppReducer';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// --- Firebase Infrastructure ---
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  getDoc,
  setDoc,
  addDoc, 
  serverTimestamp,
  orderBy,
  Timestamp,
  DocumentData
} from 'firebase/firestore';

/**
 * 🛠️ UTILITY: Deep Recursive Timestamp Conversion
 * Resolves TS2362: Corrects arithmetic errors by turning Firestore Timestamps into JS Dates.
 */
const deepConvertTimestamps = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (obj instanceof Timestamp) return obj.toDate();
  if (Array.isArray(obj)) return obj.map(deepConvertTimestamps);

  const result: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = deepConvertTimestamps(obj[key]);
    }
  }
  return result;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0); // This line scrolls the window to the very top (0,0 coordinates)
  }, [pathname]); // This effect runs every time the pathname (route) changes
  return null; // This component doesn't render any UI
};

const AppContent: React.FC = () => {
  // Explicitly typing the reducer to fix TS2345 (Payload Mismatch)
  const [state, dispatch] = useReducer(appReducer, initialState as AppState);
  const [loading, setLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false); 
  const { setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // 1. 🌓 THEME SYNC
  useEffect(() => {
    setTheme(state.theme);
  }, [state.theme, setTheme]);

  // 2. ⌨️ OMNISEARCH LISTENER (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // 3. 👤 AUTH & PROFILE INITIALIZATION
  useEffect(() => {
    let unsubUserDoc: (() => void) | null = null;
    
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
          const newUser: UserProfile = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || 'Everyday User',
            avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.displayName || 'U'}`,
            role: Role.MEMBER,
            acceptedDisclaimerVersion: '', 
            acceptedLegalVersion: '', 
            preferredLanguage: i18n.language || 'en',
            timezone: detectedTz,
            autoDetectTz: true,
            activeSparkCategories: ['vocabulary'],
            createdAt: new Date().toISOString(),
          };
          
          await setDoc(userRef, { ...newUser, createdAt: serverTimestamp() });
        }

        unsubUserDoc = onSnapshot(userRef, (docSnap) => {
          if (docSnap.exists()) {
            const userData = deepConvertTimestamps(docSnap.data());
            dispatch({ type: 'SET_USER', payload: userData as UserProfile });
            setLoading(false);
          }
        });
      } else {
        if (unsubUserDoc) unsubUserDoc();
        dispatch({ type: 'SET_USER', payload: null });
        setLoading(false);
      }
    });

    return () => { unsubAuth(); if (unsubUserDoc) unsubUserDoc(); };
  }, []);

  // 4. 🛰️ HOUSEHOLD SYNC
  useEffect(() => {
    const user = state.currentUser;
    if (!user || !user.id) return;

    const hQuery = query(collection(db, "households"), where("memberIds", "array-contains", user.id));
    const unsubHouseholds = onSnapshot(hQuery, (snapshot) => {
      if (!snapshot.empty) {
        const hList = snapshot.docs.map(docSnap => 
          deepConvertTimestamps({ id: docSnap.id, ...docSnap.data() })
        ) as Household[];
        
        dispatch({ type: 'SET_HOUSEHOLDS', payload: hList });
        
        if (!state.currentHouseholdId && hList.length > 0) {
          dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: hList[0].id });
        }
      }
    });

    return () => unsubHouseholds();
  }, [state.currentUser?.id]);

  // 5. 🛠️ ENGINE LISTENERS (Strictly Typed to fix TS7006/TS2345)
  useEffect(() => {
    const hId = state.currentHouseholdId;
    // Defensive gate to prevent null-reference errors in sub-collections
    if (!hId || !state.currentUser) return;

    // 🗓️ Calendar Listener
    const unsubCalendar = onSnapshot(query(collection(db, "households", hId, "events")), (snap) => {
      const events = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as CalendarEvent[];
      dispatch({ type: 'SET_CALENDAR_EVENTS', payload: events });
    });

    // 📋 Tasks Listener
    const unsubTasks = onSnapshot(query(collection(db, "households", hId, "tasks"), orderBy("createdAt", "desc")), (snap) => {
      const tasks = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as KanbanTask[];
      dispatch({ type: 'SET_TASKS', payload: tasks as KanbanTask[] });
    });

    // 📋 Inventory Listener
    const unsubInventory = onSnapshot(query(collection(db, "households", hId, "inventory")), (snap) => {
      const inv = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as KanbanTask[];
      dispatch({ type: 'SET_INVENTORY', payload: inv });
    });

    // 🛠️ Maintenance Listener
    const unsubMaint = onSnapshot(query(collection(db, "households", hId, "maintenance")), (snap) => {
      const maint = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as KanbanTask[];
      dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: maint });
    });

    return () => {
      unsubCalendar();
      unsubTasks();
      unsubInventory();
      unsubMaint();
    };
  }, [state.currentHouseholdId, state.currentUser?.id]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-white dark:bg-slate-950">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-indigo-600 mb-4"></div>
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Initializing OS...</p>
      </div>
    );
  }

  // --- 🔒 AUTH GATES ---
  if (!state.currentUser) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const user = state.currentUser;
  const isLegalPath = location.pathname === '/onboarding-legal';
  const isDisclaimerPath = location.pathname === '/disclaimer';

  if (user.acceptedLegalVersion !== LEGAL_VERSION && !isLegalPath) {
    return <Navigate to="/onboarding-legal" replace />;
  }

  if (user.acceptedLegalVersion === LEGAL_VERSION && user.acceptedDisclaimerVersion !== CURRENT_DISCLAIMER_VERSION && !isDisclaimerPath) {
    return <Navigate to="/disclaimer" replace />;
  }

  const activeHousehold = state.households.find(h => h.id === state.currentHouseholdId) || state.households[0];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300">
      <ScrollToTop /> {/* 🌟 It's placed here */}
      
      <SearchPortal 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        state={state} 
      />

      <Routes>
        <Route path="/onboarding-legal" element={<OnboardingLegal user={state.currentUser} dispatch={dispatch} />} />
        <Route path="/disclaimer" element={<DisclaimerPage state={state} dispatch={dispatch} />} />
        
        <Route path="*" element={
          <Layout 
            user={state.currentUser} 
            currentHousehold={activeHousehold}
            notifications={state.notifications} 
            dispatch={dispatch}
            onLogout={async () => { 
              await signOut(auth); 
              dispatch({ type: 'SET_USER', payload: null }); 
              dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: null });
              navigate('/login');
            }}
            theme={state.theme}
            onToggleTheme={() => dispatch({ type: 'TOGGLE_THEME' })}
          >
            <Routes>
              <Route path="/dashboard" element={<Dashboard state={state} dispatch={dispatch} />} />
              <Route path="/tasks" element={<Tasks state={state} dispatch={dispatch} />} />
              <Route path="/groceries" element={<Inventory state={state} dispatch={dispatch} />} />
              <Route path="/calendar" element={<Calendar state={state} dispatch={dispatch} />} />
              <Route path="/maintenance" element={<Maintenance state={state} dispatch={dispatch} />} />
              <Route path="/bug-report" element={<BugReport />} />
              <Route path="/user-settings" element={<UserSettings user={state.currentUser} state={state} dispatch={dispatch} />} />
              <Route path="/settings" element={<HouseholdSettings state={state} dispatch={dispatch} />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        } />
      </Routes>

    </div>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppContent />
  </ThemeProvider>
);

export default App;